<template>
  <div>
    <breadcrumb :items="[{
          text: isKenyaClient ? $t('approvalFlow.cooperative.unionAgents'):$t('approvalFlow.cooperative.cooperativeExporter'),
          disabled: false,
          exact: true,
        }]"></breadcrumb>
    <v-container fluid>
      <v-row align="center" class="mb-4">
        <v-col cols="12" sm="auto">
          <h2>
            {{ isKenyaClient ? $t("approvalFlow.cooperative.unionAgents") : $t('approvalFlow.cooperative.cooperativeExporter') }}
          </h2>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="auto" class="d-flex justify-sm-end">
          <v-btn color="primary" dark class="mb-2" @click="handleCreateCooperative">
            {{ isKenyaClient ? $t("approvalFlow.cooperative.addAgent") : $t("approvalFlow.cooperative.addCooperatives") }}
          </v-btn>
        </v-col>
      </v-row>
      <v-card elevation="0" class="px-5 mt-9">
        <v-card-text>
          <v-row style="margin-left:-30px; margin-bottom: 20px; margin-top: 15px;">
            <v-col cols="12">
              <div class="cooperative-table-wrapper">
              <v-data-table :headers="tableHeaders" :items="cooperatives" :items-per-page="options.limit" :footer-props="{
                'items-per-page-options': [5, 10, 25, 50],
              }" item-key="id" hide-default-footer :loading="loading" :options.sync="options"
                :server-items-length="totalCooperatives" :loading-text="$t('loadingData')
                  ">
                <template v-slot:top="{ }">
                  <v-container fluid class="pa-2 pt-3">

                    <!-- Row 1: Date filters -->
                    <v-row dense align="end" class="mb-1">
                      <!-- Date From -->
                      <v-col cols="12" sm="6" md="4" lg="2">
                        <div class="filter-label">
                          <span>{{ $t('approvalFlow.cooperative.dateFrom') }}</span>
                        </div>
                        <v-menu
                          v-model="startDateMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="startDate"
                              outlined dense hide-details readonly
                              prepend-inner-icon="mdi-calendar"
                              v-bind="attrs" v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="startDate"
                            no-title scrollable
                            @input="startDateMenu = false; handleDateChange()"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>

                      <!-- Date To -->
                      <v-col cols="12" sm="6" md="4" lg="2">
                        <div class="filter-label">
                          <span>{{ $t('approvalFlow.cooperative.dateTo') }}</span>
                        </div>
                        <v-menu
                          v-model="endDateMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="endDate"
                              outlined dense hide-details readonly
                              prepend-inner-icon="mdi-calendar"
                              v-bind="attrs" v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="endDate"
                            no-title scrollable
                            @input="endDateMenu = false; handleDateChange()"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>

                    <!-- Row 2: Search, Type, Location, Organization, Download, Pagination -->
                    <v-row dense align="end">

                      <!-- Search -->
                      <v-col cols="12" sm="6" md="4" lg="2">
                        <div class="filter-label">&nbsp;</div>
                        <v-text-field
                          prepend-inner-icon="mdi-magnify"
                          outlined dense hide-details
                          :placeholder="$t('approvalFlow.cooperative.search')"
                          v-model="search"
                          @input="searchQuery"
                        ></v-text-field>
                      </v-col>

                      <!-- Type -->
                      <v-col cols="12" sm="6" md="4" lg="2">
                        <div class="filter-label d-flex align-center">
                          <span>{{ $t('approvalFlow.cooperative.type') }}</span>
                          <v-tooltip top color="00BD73" max-width="350">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon small class="ml-1 primary--text" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                            </template>
                            <span>{{ $t("approvalFlow.cooperative.userTypeTT") }}</span>
                          </v-tooltip>
                        </div>
                        <v-autocomplete
                          clearable outlined dense hide-details
                          :placeholder="$t('approvalFlow.cooperative.all')"
                          v-model="cooperativeType"
                          item-text="name" item-value="value"
                          :items="allTypes"
                          @change="filterData"
                        ></v-autocomplete>
                      </v-col>

                      <!-- Location -->
                      <v-col cols="12" sm="6" md="4" lg="2">
                        <div class="filter-label d-flex align-center">
                          <span>{{ $t("approvalFlow.cooperative.location") }}</span>
                          <v-tooltip top color="00BD73" max-width="350">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon small class="ml-1 primary--text" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                            </template>
                            <span>{{ $t("approvalFlow.cooperative.locationTT") }}</span>
                          </v-tooltip>
                        </div>
                        <v-text-field
                          outlined dense hide-details
                          :placeholder="$t('approvalFlow.cooperative.search')"
                          v-model="locationSearch"
                          @input="searchQueryLocation"
                        ></v-text-field>
                      </v-col>

                      <!-- Organization -->
                      <v-col v-if="isSuperAdminRole" cols="12" sm="6" md="4" lg="2">
                        <div class="filter-label">
                          <span>{{ $t('approvalFlow.cooperative.organization') }}</span>
                        </div>
                        <v-autocomplete
                          clearable outlined dense hide-details
                          :placeholder="$t('approvalFlow.cooperative.selectOrganization')"
                          v-model="selectedOrganization"
                          item-text="name" item-value="id"
                          :items="organizationList"
                          :search-input.sync="organizationSearch"
                          @change="filterData"
                        ></v-autocomplete>
                      </v-col>

                      <!-- Download CSV -->
                      <v-col cols="12" sm="6" md="4" lg="auto">
                        <div class="filter-label">&nbsp;</div>
                        <v-btn
                          color="primary" dark outlined
                          class="cooperative-csv-btn"
                          :ripple="!csvDownloading"
                          @click="downloadCsv"
                        >
                          <v-progress-circular v-if="csvDownloading" indeterminate size="22" width="2" color="primary" class="mr-2" />
                          <v-icon v-else dark>mdi-download</v-icon>
                          <span class="ml-1">{{ csvDownloading ? $t('approvalFlow.cooperative.downloadingCsv') : $t('approvalFlow.cooperative.download') }}</span>
                        </v-btn>
                      </v-col>

                      <v-spacer></v-spacer>

                      <!-- Pagination + Settings -->
                      <v-col cols="12" lg="auto" class="d-flex align-end justify-end">
                        <div class="filter-label d-none d-lg-block">&nbsp;</div>
                        <div class="d-flex align-center">
                          <span class="title">
                            {{ fromCooperativesList }} – {{ toCooperativesList }} {{ $t('approvalFlow.cooperative.of') }} {{ totalCooperatives }}
                          </span>
                          <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page <= 1" @click="reportPageChange(false)">
                            <v-icon dark>mdi-chevron-left</v-icon>
                          </v-btn>
                          <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page >= totalPages" @click="reportPageChange(true)">
                            <v-icon dark>mdi-chevron-right</v-icon>
                          </v-btn>
                          <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left :min-width="417">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                                <v-icon dark>mdi-cog-outline</v-icon>
                              </v-btn>
                            </template>
                            <v-card width="417">
                              <v-list>
                                <v-list-item>
                                  <v-list-item-content>
                                    <v-list-item-title>{{ $t("deforestation.customization") }}</v-list-item-title>
                                  </v-list-item-content>
                                </v-list-item>
                                <v-divider></v-divider>
                                <div class="pl-3 pt-2 font-weight-normal">{{ $t("deforestation.recordsPerpage") }}</div>
                                <v-list-item>
                                  <v-list-item-content>
                                    <v-slider v-model="itemsPerPageSlider" :tick-labels="itemsPerPageOptions" :max="3" step="1" ticks="always" tick-size="4"></v-slider>
                                  </v-list-item-content>
                                </v-list-item>
                                <v-divider class="mt-n3"></v-divider>
                              </v-list>
                              <v-card-text>
                                <v-container fluid>
                                  <v-row>
                                    <v-col class="mt-n9" cols="6" v-for="header in headers" :key="header.value">
                                      <v-checkbox v-if="header.text" v-model="selectedHeaders" :label="header.text" color="primary" :value="header.text" hide-details :disabled="header.text == 'ID'"></v-checkbox>
                                    </v-col>
                                  </v-row>
                                </v-container>
                              </v-card-text>
                              <v-layout justify-center>
                                <v-card-actions class="mb-5">
                                  <v-spacer></v-spacer>
                                  <v-btn outlined color="primary" @click="resetDefaut" width="190">
                                    <span class="text-truncate" style="max-width:150px">{{ $t("deforestation.restoreDefault") }}</span>
                                  </v-btn>
                                  <v-btn color="primary" @click="resetTableStructure" width="190">{{ $t("deforestation.apply") }}</v-btn>
                                </v-card-actions>
                              </v-layout>
                            </v-card>
                          </v-menu>
                        </div>
                      </v-col>

                    </v-row>
                  </v-container>
                </template>
                <template v-if="cooperatives.length" v-slot:body="{ items }">
                  <tbody>
                    <tr v-for="item in items" :key="item.id" style="position: relative; width: 0; height: 0"
                      @mouseover="selectItem(item)" @mouseleave="selectItem(false)">
                      <template v-for="h in tableHeaders">

                        <td class="text-truncate" :key="h.value + '_cooperative'" v-if="h.value == 'cooperative'">
                          <div class="d-flex align-center">
                            <v-avatar size="32" class="mr-3">
                              <v-img :src="item.logo" :alt="item.name"></v-img>
                            </v-avatar>
                            <div>
                              <div class="font-weight-medium">{{ item.name }}</div>
                              <div class="caption text-muted">{{ item.type }}</div>
                            </div>
                          </div>
                        </td>

                        <td class="text-truncate" :key="h.value + '_primaryContact'"
                          v-else-if="h.value == 'primaryContact'">
                          <div>
                            <div class="font-weight-medium">{{ item.primaryContact.name }}</div>
                            <div class="caption text-muted">{{ item.primaryContact.email }}</div>
                            <div class="caption text-muted">{{ item.primaryContact.phone }}</div>
                          </div>
                        </td>

                        <td class="text-truncate" :key="h.value + '_noOfFarms'" v-else-if="h.value == 'noOfFarms'">
                          {{ item.numberOfFarms }}
                        </td>

                        <td class="text-truncate" :key="h.value + '_cropsProducts'"
                          v-else-if="h.value == 'cropsProducts'">
                          {{ item.cropsProducts }}
                        </td>
                        <td class="text-truncate" :key="h.value + '_location'" v-else-if="h.value == 'location'">
                          {{ item.location.length > 30 ? item.location.substring(0, 30) + '...' : item.location }}
                        </td> 

                        <td class="text-truncate" :key="h.value + '_registrationDate'" v-else-if="h.value == 'registrationDate'">
                          {{ item.registrationDate }}
                        </td>

                        <td class="text-truncate" :key="h.value + '_status'" v-else-if="h.value == 'status'">
                          <v-btn small rounded min-width="100" depressed class="green-button" height="32"
                            v-if="item.status === 'active'">
                            {{ $t("approvalFlow.cooperative.active") }}
                          </v-btn>
                          <v-btn small rounded min-width="100" depressed class="gray-button" height="32"
                            v-else-if="item.status === 'unregistered'">
                            {{ $t("approvalFlow.cooperative.unregistered") }}
                          </v-btn>
                          <v-btn small rounded min-width="100" depressed class="red-button" height="32"
                            v-else-if="item.status === 'deactivated'">
                            {{ $t("approvalFlow.cooperative.deactivated") }}
                          </v-btn>
                        </td>
                        <td :key="h.value + '_action'" v-else-if="h.value == 'action'">
                          <div style="position: absolute; right: 10px; top:10px;">
                            <v-menu location="start">
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon v-bind="attrs" v-on="on">
                                  <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                              </template>
                              <v-list dense style="min-width: 150px;">

                                <v-list-item v-if="!item.isMainOrg" @click="viewCooperative(item)">
                                  <v-list-item-title>{{ $t('approvalFlow.cooperative.view') }}</v-list-item-title>
                                </v-list-item>
                                <!-- <v-list-item @click="editCooperative(item)">
                                  <v-list-item-title>{{ $t('approvalFlow.cooperative.edit') }}</v-list-item-title>
                                </v-list-item> -->
                                <v-list-item
                                  :disabled="farmDownloadingId === (item.subOrgId || item.id)"
                                  @click="downloadCooperativeFarms(item)"
                                >
                                  <v-list-item-title class="d-flex align-center">
                                    <v-progress-circular
                                      v-if="farmDownloadingId === (item.subOrgId || item.id)"
                                      indeterminate size="14" width="2" color="primary" class="mr-2"
                                    />
                                    <v-icon v-else small class="mr-2">mdi-download</v-icon>
                                    {{ $t('approvalFlow.cooperative.downloadFarms') }}
                                  </v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="deactivateCooperativeMethod(item.id,item.status)" color="red">
                                  <v-list-item-title :class="item.status === 'deactivate' ? 'black--text' : 'red--text'">{{ $t(item.status === 'active' ? 'Deactivate' : 'Activate') }}</v-list-item-title>
                                </v-list-item>
                                <!-- <v-list-item @click="deleteCooperative(item.id)">
                                  <v-list-item-title class="red--text">{{ $t('approvalFlow.cooperative.delete') }}</v-list-item-title>
                                </v-list-item> -->
                              </v-list>
                            </v-menu>
                          </div>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </template>

                <template v-slot:footer></template>
              </v-data-table>
              </div>

            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>

    <ConfirmBox :title="confirmDelete.title" :message="confirmDelete.message"
      :cancelBtnText="confirmDelete.cancelBtnText" :confirmBtnText="confirmDelete.confirmBtnText"
      :confirmData="confirmDelete.confirmData" :dialog="confirmDeleteDialog"
      @cancel:action="confirmDeleteDialog = false" @continue:action="confirmDeleteCooperative" />

    <AddCooprateDialog :dialog="addEditDialog" :item="editingItem" @close="onCloseDialog" @save="onSaveCooperative" />
  </div>


</template>
<script>
import loadingMixin from "@/mixins/LoadingMixin";
import download from "@/mixins/DownloadMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import DateMixin from "@/mixins/DateMixin";
import ConfirmBox from "@/components/ConfirmBox";
import AddCooprateDialog from "@/views/deforestation/dds/approval-flow/cooperatives/components/AddCooprateDialog.vue";
import moment from 'moment';
import RoleMixin from "@/mixins/RoleMixin";
import { mapGetters } from "vuex";
import ApprovalFlowService from "@/_services/ApprovalFlowService";
import { isKenyaClient } from "@/utils";
import CarbonCreditService from "@/_services/CarbonCreditService";

export default {
  components: {
    ConfirmBox,
    AddCooprateDialog,
  },
  created() {
    if (this.getOrganizationId) {
      this.selectedOrganization = this.getOrganizationId;
    }
    this.getOrganizationLists();
    this.getCooperatives();
  },
  async mounted() {
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
  },
  computed: {
    ...mapGetters("eudrSettings", ["get_EUDR_Settings"]),
    isKenyaClient() {
      return isKenyaClient();
    },
    isOwner() {
      return this.getCurrentRoles.some(x => ["operator_owner", "supplier_owner"].includes(x))
    },
    isSuperAdminRole() {
      return this.getCurrentRoles.some(x => ["superadmin_cooperative"].includes(x))
    },
    getOrganizationId() {
      return this.$store.getters.getUser?.user_organization?.id;
    },
    reportHeaders() {
      return this.headers.filter(header => this.selectedHeaders.includes(header.text));
    },
    totalPages() {
      const perPage = this.options.limit || 10;
      return Math.max(1, parseInt(Math.ceil(this.totalCooperatives / perPage)));
    },
    eudrSettings() {
      return this.get_EUDR_Settings;
    },
    headers(){
      return this.isKenyaClient ? this.headersr.map(header => {
        if (header.value === 'cooperative') {
          return { ...header, text: this.$t('approvalFlow.cooperative.unionAgents') };
        }
        return header;
      }) : this.headersr;
    }
  },
  data() {
    return {
      debounceTimer: null,
      fromCooperativesList: 0,
      toCooperativesList: 0,
      search: "",
      locationSearch: "",
      cooperativeType: "",
      copyReport: [],
      loading: true,
      selectedItem: false,
      options: {
        limit: 10,
        page: 1,
      },
      headersr: [
        {
          text: this.$t("approvalFlow.cooperative.cooperative"),
          align: "start",
          value: "cooperative",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("approvalFlow.cooperative.primaryContact"),
          align: "start",
          value: "primaryContact",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("approvalFlow.cooperative.noOfFarms"),
          align: "start",
          value: "noOfFarms",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("approvalFlow.cooperative.cropsProducts"),
          align: "start",
          value: "cropsProducts",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("approvalFlow.cooperative.location"),
          align: "start",
          value: "location",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("approvalFlow.cooperative.registrationDate"),
          align: "start",
          value: "registrationDate",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("approvalFlow.cooperative.status"),
          align: "start",
          value: "status",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("approvalFlow.cooperative.actions"),
          align: "center",
          value: "action",
          class: "black--text",
          sortable: false,
        },
      ],
      allSelectedHeaders: [
        this.$t('approvalFlow.cooperative.cooperative'),
        this.$t('approvalFlow.cooperative.primaryContact'),
        this.$t('approvalFlow.cooperative.noOfFarms'),
        this.$t("approvalFlow.cooperative.cropsProducts"),
        this.$t("approvalFlow.cooperative.location"),
        this.$t("approvalFlow.cooperative.registrationDate"),
        this.$t("approvalFlow.cooperative.status"),
        this.$t("approvalFlow.cooperative.actions"),
      ],
      menu: false,
      cooperatives: [],
      totalCooperatives: 0,
      totalCooperativePages: 0,
      showActionsColumn: true,
      customizeMenu: false,
      customizeMenuAll: false,
      itemsPerPageOptions: [5, 10, 25, 50],
      itemsPerPageSlider: 1,
      selectedHeaders: [],
      tableHeaders: [],
      hideOnPrint: false,
      confirmDeleteDialog: false,
      confirmDelete: {
        title: this.$t("approvalFlow.cooperative.confirmDelete"),
        message: this.$t("approvalFlow.cooperative.areYouSureDeleteCooperative"),
        confirmBtnText: this.$t("approvalFlow.cooperative.delete"),
        cancelBtnText: this.$t("approvalFlow.cooperative.cancel"),
        confirmData: {},
      },
      allTypes: [
        {
          name: this.$t("approvalFlow.cooperative.all"),
          value: "",
        },
        { name: this.$t("approvalFlow.cooperative.cooperative"), value: "cooperative" },
        { name: this.$t("approvalFlow.cooperative.exporter"), value: "exporter" },
        { name: this.$t("approvalFlow.cooperative.cooperativeAndExporter"), value: "cooperative and exporter" },
      ],
      addEditDialog: false,
      editingItem: null,
      orderField: 'createdAt',
      order: 'DESC',
      csvDownloading: false,
      farmDownloadingId: null,
      selectedOrganization: null,
      organizationSearch: "",
      organizationList: [],
      startDate: (() => { const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
      endDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
      startDateMenu: false,
      endDateMenu: false,
    };
  },
  methods: {
    formatLocalDate(date) {
      const d = date instanceof Date ? date : new Date(date);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },

    async resetDefaut() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.menu = false;
      await this.getCooperatives();
    },

    async resetTableStructure() {
      this.tableHeaders = this.headers.filter((header) =>
        this.selectedHeaders.includes(header.text)
      );
      this.options.limit = this.itemsPerPageOptions[
        this.itemsPerPageSlider
      ];
      await this.getCooperatives();
      (this.customizeMenu = false), (this.customizeMenuAll = false);
    },

    getDateFormat(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    viewCooperative(item) {
      this.$router.push(`/approval-flow/cooperatives/view/${item.id}`);
    },

    async editCooperative(item) {
      try {
        this.startLoading();
        const response = await ApprovalFlowService.getCooperativeDetails(item.id);
        if (response.success && response.data) {
          this.editingItem = response.data;
          this.addEditDialog = true;
        } else {
          this.$notify({
            text: this.$t('approvalFlow.cooperative.errorFetchingCooperativeDetails'),
            type: 'error'
          });
        }
      } catch (error) {
        console.error('Error fetching cooperative details:', error);
        this.$notify({
          text: this.$t('approvalFlow.cooperative.errorFetchingCooperativeDetails'),
          type: 'error'
        });
      } finally {
        this.stopLoading();
      }
    },

    handleCreateCooperative() {
      this.editingItem = null;
      this.addEditDialog = true;
    },

    onCloseDialog() {
      this.addEditDialog = false;
      this.editingItem = null;
    },
    async onSaveCooperative(payload) {
      try {
        this.startLoading();
        let response;
        if (payload.id) {
          response = await ApprovalFlowService.updateCooperative(payload.id, payload);
        } else {
          const createPayload = {
            ...payload,
            ...(this.isSuperAdminRole && this.selectedOrganization
              ? { organizationId: this.selectedOrganization }
              : {}),
          };
          response = await ApprovalFlowService.addCooperative(createPayload);
        }

        if (response.success) {
          this.$notify({
            text: payload.id ? this.$t('approvalFlow.cooperative.updatedSuccessfully') : this.$t('approvalFlow.cooperative.createdSuccessfully'),
            type: 'success'
          });

          // Ensure newly created row is visible: clear type filter, widen dates, page 1
          if (!payload.id) {
            this.cooperativeType = '';
            this.options.page = 1;
            const today = this.formatLocalDate(new Date());
            const weekAgo = this.formatLocalDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
            this.startDate = weekAgo;
            this.endDate = today;
          }

          await this.getCooperatives();
          this.onCloseDialog();
        } else {
          this.$notify({
            text: response.message || this.$t('approvalFlow.cooperative.errorSavingCooperative'),
            type: 'error'
          });
        }
      } catch (error) {
        this.$notify({
          text: `Try with Unique Email or ${error.message}` || this.$t('approvalFlow.cooperative.errorSavingCooperative'),
          type: 'error'
        });
      } finally {
        this.stopLoading();
      }
    },

    filterData() {
      this.options.page = 1;
      this.getCooperatives();
    },
    searchQuery() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.getCooperatives();
      }, 500)
    },
    async getCooperatives() {
      this.loading = true;

      try {
        const params = Object.fromEntries(
          this.buildCooperativeListQueryParams({ includePagination: true })
        );

        const response = await ApprovalFlowService.getCoorporativeLists(params);

        if (response.success) {
          // Map API response
          this.cooperatives = response.data.rows.map(item => ({
            id: item.id,
            subOrgId: item.subOrg?.id || null,
            name: item.subOrg?.name || item.fullName,
            type: item.translatedRegistrationType,
            logo:  item.subOrg?.logo || item.org?.logo,
            primaryContact: {
              name: item.fullName,
              email: item.email,
              phone: item.mobile || ''
            },
            numberOfFarms: item.farmsCount || 0,
            cropsProducts: item.subOrg?.products?.map(p => p.name).join(', ') || 'N/A',
            location: item.address || 'N/A',
            registrationDate: item.createdAt ? new Date(item.createdAt).toISOString().substring(0, 10) : 'N/A',
            status: item.active ? 'active' : 'deactivated'
          }));


            let mainOrg = response.data?.org;
            let orgFarmCount = response.data?.orgFarmCount[0];
            const mainOrganization = {
              id: mainOrg?.id,
              name: mainOrg?.name || this.$t('approvalFlow.cooperative.mainOrganization'),
              type: 'Main',
              logo: mainOrg?.logo,
              primaryContact: { 
                name: mainOrg?.primaryUser ? `${mainOrg.primaryUser.firstName} ${mainOrg.primaryUser.lastName}` : 'N/A',
                email: mainOrg?.primaryUser?.email || 'N/A',
                phone: mainOrg?.primaryUser?.phone || 'N/A'
              },
              numberOfFarms: orgFarmCount?.farmCount || 0,
              cropsProducts: mainOrg?.products?.map(p => p.name).join(', ') || 'N/A',
              location: mainOrg?.address || 'N/A',
              registrationDate: mainOrg?.createdAt ? new Date(mainOrg.createdAt).toISOString().substring(0, 10) : 'N/A',
              status: mainOrg?.status,
              isMainOrg: true,
            }
            if(this.options.page === 1){
              this.cooperatives.unshift(mainOrganization); // Add main organization at the beginning of the list
            }
          
          this.totalCooperatives = response.data.totalCount + 1; // Adding 1 to include the main organization

          

          const perPage = this.options.limit;
          const currentPage = this.options.page;
          this.fromCooperativesList = this.totalCooperatives > 0
            ? (currentPage - 1) * perPage + 1
            : 0;
          this.toCooperativesList = currentPage * perPage > this.totalCooperatives
            ? this.totalCooperatives
            : currentPage * perPage;
        }
      } catch (error) {
        console.error('Error fetching cooperatives:', error);
        this.$notify({
          text: this.$t('approvalFlow.cooperative.errorFetchingCooperativesData'),
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    getOrganizationLists() {
      CarbonCreditService.searchOrganizations().then(res => {
        console.log('Organization List:', res);
        this.organizationList = res.data.map(org => ({
          id: org.id,
          name: org.name
        }));
      }).catch(err=>{
        console.error('Error fetching organization list:', err);
      })
    },
    getCooperativeType(userRoles) {
      if (!userRoles || userRoles.length === 0) return 'Cooperative';

      const roleIds = userRoles.map(role => role.id);

      if (roleIds.includes('supplier_owner') && roleIds.includes('indonesia_admin')) {
        return 'Cooperative + Exporter';
      } else if (roleIds.includes('supplier_owner')) {
        return 'Exporter';
      } else if (roleIds.includes('indonesia_admin')) {
        return 'Cooperative';
      }

      return 'Cooperative';
    },
    async deactivateCooperativeMethod(id, status) {
      this.status = status === 'active' ? 'deactivate' : 'activate';
      try {
        this.startLoading();
        const response = await ApprovalFlowService.deactivateCooperative(id, this.status);
        if (response.success) {
          this.$notify({
            text: this.$t('approvalFlow.cooperative.cooperativeDeactivatedSuccessfully'),
            type: 'success'
          });
        }
        await this.getCooperatives();
      } catch (error) {
        console.error('Error deactivating cooperative:', error);
        this.$notify({
          text: this.$t('approvalFlow.cooperative.errorDeactivatingCooperative'),
          type: 'error'
        });
      } finally {
        this.stopLoading();
      }
    },
    async deleteCooperative(id) {
      try {
        this.startLoading();
        const response = await ApprovalFlowService.deleteCooperative(id);
        if (response.success) {
          this.$notify({
            text: this.$t('approvalFlow.cooperative.cooperativeDeletedSuccessfully'),
            type: 'success'
          });
        }
        await this.getCooperatives();
      } catch (error) {
        console.error('Error deleting cooperative:', error);
        this.$notify({
          text: this.$t('approvalFlow.cooperative.errorDeletingCooperative'),
          type: 'error'
        });
      } finally {
        this.stopLoading();
      }
    },
    async confirmDeleteCooperative(action, confirmData) {
      this.startLoading();

      try {
        // TODO: Implement actual delete API call
        this.startLoading();
        await ApprovalFlowService.deleteCooperative(confirmData.id);

        // For now, just refresh the list
        await this.getCooperatives();

        this.$notify({
          text: this.$t("approvalFlow.cooperative.cooperativeDeletedSuccessfully"),
          type: "success",
        });
      } catch (error) {
        console.error('Error deleting cooperative:', error);
        this.$notify({
          text: this.$t('approvalFlow.cooperative.errorDeletingCooperative'),
          type: 'error'
        });
        } finally {
        this.stopLoading();
      }
    },

    selectItem(item) {
      this.selectedItem = item;
    },
    searchQueryLocation() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.getCooperatives();
      }, 500)
    },

    handleDateChange() {
      this.options.page = 1;
      this.getCooperatives();
    },

    buildCooperativeListQueryParams({ includePagination } = { includePagination: true }) {
      const params = {
        registrationType: this.cooperativeType,
        searchPhrase: this.search,
        address: this.locationSearch,
        orderField: this.orderField,
        order: this.order,
        startDate: this.startDate,
        endDate: this.endDate,
        ...(this.isSuperAdminRole ? { organizationId: this.selectedOrganization } : {})
      };
      if (includePagination) {
        params.page = this.options.page;
        params.limit = this.options.limit;
      }
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          queryParams.append(key, value);
        }
      });
      return queryParams;
    },

    async downloadCsv() {
      if (this.csvDownloading) return;
      const queryParams = this.buildCooperativeListQueryParams({ includePagination: false });
      const path = ApprovalFlowService.DOWNLOAD_COOPERATIVES_CSV_URL;
      const url = queryParams.toString() ? `${path}?${queryParams.toString()}` : path;
      const csvFilename = this.isKenyaClient ? "unions.csv" : "cooperatives.csv";
      this.csvDownloading = true;
      try {
        await this.downloadSourceFile(url, "text/csv", csvFilename);        this.$notify({
          text: this.$t("approvalFlow.cooperative.csvExportSuccess"),
          type: "success",
        });
      } catch (err) {
        console.error("Cooperative CSV export failed:", err);
        this.$notify({
          text: this.$t("approvalFlow.cooperative.csvExportError"),
          type: "error",
        });
      } finally {
        this.csvDownloading = false;
      }
    },
    async downloadCooperativeFarms(item) {
      // Main org row uses its own org ID with type=org;
      // sub-org rows use the subOrg ID with type=sub-org (default).
      const cooperativeId = item.isMainOrg ? item.id : (item.subOrgId || item.id);
      const type = item.isMainOrg ? 'org' : 'sub-org';

      if (!cooperativeId || this.farmDownloadingId === cooperativeId) return;

      this.farmDownloadingId = cooperativeId;
      try {
        const params = { type };
        if (this.startDate) params.startDate = this.startDate;
        if (this.endDate) params.endDate = this.endDate;

        const response = await ApprovalFlowService.getCooperativeFarms(cooperativeId, params);

        if (!response.success || !response.data?.length) {
          this.$notify({ text: this.$t('approvalFlow.cooperative.noFarmsFound'), type: 'info' });
          return;
        }

        const farms = response.data;
        const cooperativeName = (item.name || 'cooperative').replace(/[^a-z0-9]/gi, '-').toLowerCase();
        const filename = `${cooperativeName}-farms.csv`;

        // Build CSV
        const formatCoords = (coords) => {
          if (!Array.isArray(coords) || !coords.length) return '';
          return coords.map(c => `${c.lat},${c.log}`).join('|');
        };

        const escape = (val) => {
          if (val === null || val === undefined) return '';
          const str = String(val).replace(/"/g, '""');
          return `"${str}"`;
        };

        const headers = [
          'Farm ID', 'Farm Name', 'Registration No',
          'Farmer First Name', 'Farmer Last Name', 'Farmer Email', 'Farmer Mobile',
          'Address', 'Country', 'State', 'City', 'Region',
          'Area', 'Farming Activity', 'Ownership Type', 'Production Type',
          'Status', 'Created At', 'Center Lat', 'Center Log',
          'Farm Coordinates', 'Zones'
        ];

        const rows = farms.map(farm => {
          const zonesStr = (farm.zones || []).map(z =>
            `${z.geofenceName || ''}:[${formatCoords(z.coordinates)}]`
          ).join('; ');

          return [
            escape(farm.id),
            escape(farm.farmName),
            escape(farm.registrationNo),
            escape(farm.farmerFirstName),
            escape(farm.farmerLastName),
            escape(farm.user?.email),
            escape(farm.user?.mobile),
            escape(farm.address),
            escape(farm.country),
            escape(farm.state),
            escape(farm.city),
            escape(farm.region),
            escape(farm.area),
            escape(farm.farmingActivity),
            escape(farm.farmOwnershipType),
            escape(farm.productionType),
            escape(farm.status),
            escape(farm.createdAt ? new Date(farm.createdAt).toISOString().substring(0, 10) : ''),
            escape(farm.lat),
            escape(farm.log),
            escape(formatCoords(farm.coordinates)),
            escape(zonesStr),
          ].join(',');
        });

        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$notify({ text: this.$t('approvalFlow.cooperative.csvExportSuccess'), type: 'success' });
      } catch (err) {
        console.error('Error downloading cooperative farms:', err);
        this.$notify({ text: this.$t('approvalFlow.cooperative.csvExportError'), type: 'error' });
      } finally {
        this.farmDownloadingId = null;
      }
    },

    resetSearchOnEmpty() {
      this.search = "";
      this.locationSearch = "";
      this.cooperativeType = "";
      this.options = {
        page: 1,
        limit: 10,
      };
      this.getCooperatives();
    },
    reportPageChange(t) {
      if (t) this.options.page = this.options.page + 1;
      else this.options.page = this.options.page - 1;
      this.getCooperatives();
    },
  },
  mixins: [loadingMixin, download, getPermittedActions, DateMixin, RoleMixin],
};
</script>


<style lang="scss" scoped>
.blue-button {
  background-color: #d0e6fe !important;
  /* Blue background */
  border: 2px solid #002287 !important;
  /* Blue border */
  color: #002287 !important;
}

.orange-button {
  background-color: #fff7ec !important;
  /* Orange background */
  border: 2px solid #ffa826 !important;
  /* Orange border */
  color: #ffa826 !important;
}

.green-button {
  background-color: #e8f5e8 !important;
  /* Green background */
  border: 2px solid #50b053 !important;
  /* Green border */
  color: #4caf50 !important;
}

.red-button {
  background-color: #ffeaea !important;
  /* Red background */
  border: 2px solid #f44336 !important;
  /* Red border */
  color: #f44336 !important;
}

::v-deep .wdt.v-text-field {
  width: 250px;
}

::v-deep .v-tab:hover {
  background-color: #caf1e2;
  color: $primary_color !important;
}

::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: black;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep .v-data-table-header th {
  white-space: nowrap !important;
  min-width: fit-content;
}

::v-deep .v-data-table-header th:nth-child(3) {
  min-width: 120px;
}

::v-deep .v-data-table-header th:nth-child(4) {
  min-width: 150px;
}

::v-deep .center-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .img-center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 40%;
}

.gray-button {
  background-color: #e8e8e8 !important;
  /* Gray background */
  border: 2px solid #000000 !important;
  /* Black border */
  color: #000000 !important;
}

/* Keep export control visible while CSV is generating (avoid VBtn :loading empty slot collapse). */
.cooperative-csv-btn {
  min-width: 200px;
  height: 40px !important;
}

.filter-label {
  font-size: 12px;
  min-height: 20px;
  line-height: 20px;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.6);
}

.cooperative-table-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.cooperative-table-wrapper ::v-deep .v-data-table__wrapper {
  overflow-x: visible;
}

::v-deep .cooperative-csv-btn .v-btn__content {
  justify-content: center;
}
</style>